import { useEffect, useState } from 'react';
import { Panel, Flex, Button, CellList, CellSimple, CellHeader, Typography, Spinner } from '@maxhub/max-ui';
import { getReceiptPreview, getReceiptQrUrl } from '../api/receipts.ts';
import { formatPeriod } from '../utils/period.ts';
import type { Receipt } from '../types/models.ts';

interface ReceiptViewPageProps {
  receiptId: string;
  onBack: () => void;
}

export function ReceiptViewPage({ receiptId, onBack }: ReceiptViewPageProps) {
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    getReceiptPreview(receiptId)
      .then(setReceipt)
      .catch(() => setError('Не удалось загрузить квитанцию'))
      .finally(() => setLoading(false));
  }, [receiptId]);

  if (loading) {
    return (
      <Panel centeredX centeredY style={{ minHeight: '100vh' }}>
        <Spinner size={24} />
      </Panel>
    );
  }

  if (error || !receipt) {
    return (
      <Panel centeredX centeredY style={{ minHeight: '100vh' }}>
        <Typography.Label style={{ display: 'block', opacity: 0.6 }}>
          {error ?? 'Квитанция не найдена'}
        </Typography.Label>
      </Panel>
    );
  }

  const cold = Number(receipt.coldConsumption);
  const hot = Number(receipt.hotConsumption);
  const wastewater = Number(receipt.totalWastewater);
  const coldCost = Number(receipt.coldWaterCost);
  const hotCost = Number(receipt.hotWaterCost);
  const wastewaterCost = Number(receipt.wastewaterCost);
  const total = Number(receipt.totalAmount);

  return (
    <Panel style={{ padding: '1rem', maxWidth: 480, margin: '0 auto' }}>
      {/* Header */}
      <Flex justify="space-between" align="center" style={{ marginBottom: '0.75rem' }}>
        <Button mode="link" onClick={onBack}>← Назад</Button>
        <Typography.Headline variant="medium-strong">Квитанция</Typography.Headline>
        <div style={{ width: 60 }} />
      </Flex>

      {/* Период и номер */}
      <div style={{ marginBottom: '1rem' }}>
        <Typography.Headline variant="medium-strong" style={{ display: 'block' }}>
          {formatPeriod(receipt.period)}
        </Typography.Headline>
        <Typography.Label style={{ display: 'block', opacity: 0.5, marginTop: '0.2rem' }}>
          № {receipt.receiptNumber}
        </Typography.Label>
      </div>

      {/* Расчёт */}
      <CellList mode="island" style={{ marginBottom: '1rem' }}>
        <CellHeader>Расчёт</CellHeader>
        <CellSimple
          title="Холодная вода"
          subtitle={`${cold.toFixed(2)} м³ × ${Number(receipt.coldWaterRate).toFixed(4)}`}
          after={<span style={{ fontWeight: 600 }}>{coldCost.toFixed(2)} ₽</span>}
        />
        <CellSimple
          title="Горячая вода"
          subtitle={`${hot.toFixed(2)} м³ × ${Number(receipt.hotWaterRate).toFixed(4)}`}
          after={<span style={{ fontWeight: 600 }}>{hotCost.toFixed(2)} ₽</span>}
        />
        <CellSimple
          title="Водоотведение"
          subtitle={`${wastewater.toFixed(2)} м³ × ${Number(receipt.wastewaterRate).toFixed(4)}`}
          after={<span style={{ fontWeight: 600 }}>{wastewaterCost.toFixed(2)} ₽</span>}
        />
        <div style={{ borderTop: '2px solid var(--color-separator, #eee)', margin: '0 1rem' }} />
        <CellSimple
          title={<strong>Итого к оплате</strong>}
          after={
            <Typography.Headline variant="medium-strong" style={{ color: '#2e7d32' }}>
              {total.toFixed(2)} ₽
            </Typography.Headline>
          }
        />
      </CellList>

      {/* QR-код */}
      <CellList mode="island" style={{ marginBottom: '1rem' }}>
        <CellHeader>QR-код для оплаты</CellHeader>
        <div style={{ padding: '1rem', textAlign: 'center' }}>
          <img
            src={receipt.qrCodeUrl ?? getReceiptQrUrl(receiptId)}
            alt="QR-код для оплаты"
            style={{ width: 160, height: 160, display: 'block', margin: '0 auto 0.5rem' }}
          />
          <Typography.Label style={{ display: 'block', opacity: 0.5 }}>
            Отсканируйте в приложении банка
          </Typography.Label>
        </div>
      </CellList>

      {/* Кнопка PDF — используем прямой путь к статике, чтобы избежать 401 на <a> без токена */}
      {receipt.pdfUrl && (
        <Button mode="primary" stretched asChild>
          <a href={receipt.pdfUrl} target="_blank" rel="noreferrer">
            Скачать PDF
          </a>
        </Button>
      )}
    </Panel>
  );
}
